import type { PetType } from './cartSnapshot';

/**
 * An add whose `Added Item To Cart` is waiting for the bag to price the cart. The pages the main app serves leave
 * for the bag by a full page load, which ends that wait, so the add is kept here until one page sends it: the page
 * that made it, if the pricing arrives there, or else the bag.
 */
export interface PendingAddedItemToCart {
  cartItemId: string;
  sku: string;
  petType?: PetType;
  /** the document that made the add; the bag leaves its own document's adds to the add itself */
  page: string;
}

const STORAGE_KEY = 'fi_pending_added_item_to_cart';
const THIS_PAGE = Math.random().toString(36).slice(2);

function read(): PendingAddedItemToCart[] {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function write(pending: PendingAddedItemToCart[]) {
  try {
    if (pending.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    return true;
  } catch {
    return false;
  }
}

/** Returns false when storage is unavailable, and the event can only be sent from this page. */
export function savePendingAddedItemToCart(added: Omit<PendingAddedItemToCart, 'page'>) {
  return write([...read(), { ...added, page: THIS_PAGE }]);
}

/** Removes the add, and says whether it was still pending, so that only the first page to claim it sends it. */
export function claimPendingAddedItemToCart(cartItemId: string) {
  const pending = read();
  return (
    pending.some((added) => added.cartItemId === cartItemId) &&
    write(pending.filter((added) => added.cartItemId !== cartItemId))
  );
}

/** The adds earlier pages left unsent. */
export function pendingAddsFromEarlierPages() {
  return read().filter((added) => added.page !== THIS_PAGE);
}
